import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Alert,
    AlertTitle,
    Box,
    Button,
    Card,
    Checkbox,
    Chip,
    CircularProgress,
    Divider,
    Drawer,
    FormControlLabel,
    IconButton,
    Pagination,
    Stack,
    TextField,
    Tooltip,
    Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import MapOutlinedIcon from "@mui/icons-material/MapOutlined";
import PlaceOutlinedIcon from "@mui/icons-material/PlaceOutlined";
import SpaOutlinedIcon from "@mui/icons-material/SpaOutlined";
import StraightenOutlinedIcon from "@mui/icons-material/StraightenOutlined";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { DatePickerToolbar } from "@mui/x-date-pickers/DatePicker";
import { usePickerActionsContext } from "@mui/x-date-pickers/hooks";
import api from "../lib/api";
import AddFieldDialog from "./AddFieldDialog";
import AppSnackbar from "./AppSnackbar";
import DeleteConfirmDialog from "./DeleteConfirmDialog";

const FIELDS_PER_PAGE = 6;
const CROP_OPTIONS = [
    { value: "wheat", label: "Wheat" },
    { value: "barley", label: "Barley" },
    { value: "oat", label: "Oat" },
];

const yellowButtonSx = {
    "&&": {
        bgcolor: "#f7b900", backgroundImage: "none", color: "#312600",
        borderColor: "#f7b900", boxShadow: "none", fontWeight: 600, textTransform: "none",
    },
    "&&:hover, &&:focus-visible": {
        bgcolor: "#dca600", color: "#312600", borderColor: "#dca600", boxShadow: "none",
    },
};

const yellowOutlinedButtonSx = {
    textTransform: "none",
    borderColor: "#f7b900",
    color: "#7a5900",
    fontWeight: 600,
    "&:hover": { borderColor: "#dca600", bgcolor: "#fff7d6" },
};

const cycleFieldSx = {
    "&& .MuiOutlinedInput-root": {
        bgcolor: "#fff",
        boxShadow: "none !important",
        "& fieldset": { borderColor: "#e1b92f !important" },
        "&:hover fieldset": { borderColor: "#dca600 !important" },
        "&.Mui-focused fieldset": { borderColor: "#f7b900 !important", borderWidth: "2px !important" },
    },
    "&& .MuiOutlinedInput-notchedOutline": { borderColor: "#e1b92f !important" },
    "&& .Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#f7b900 !important" },
    "&& .MuiPickersOutlinedInput-root": {
        bgcolor: "#fff",
        boxShadow: "none !important",
        "& .MuiPickersOutlinedInput-notchedOutline": { borderColor: "#e1b92f !important" },
        "&:hover .MuiPickersOutlinedInput-notchedOutline": { borderColor: "#dca600 !important" },
        "&.Mui-focused .MuiPickersOutlinedInput-notchedOutline": {
            borderColor: "#f7b900 !important",
            borderWidth: "2px !important",
        },
    },
    "& .MuiInputLabel-root.Mui-focused": { color: "#9a7000 !important" },
};

const yellowCalendarPopperSx = {
    "& .MuiPaper-root": { border: "1px solid #efd36c" },
    "& .MuiPickersDay-root:hover": { bgcolor: "#fff7d6" },
    "& .MuiPickersDay-root.Mui-selected": {
        bgcolor: "#f7b900 !important",
        color: "#312600 !important",
        "&:hover, &:focus": { bgcolor: "#dca600 !important" },
    },
    "& .MuiPickersDay-today": { borderColor: "#f7b900 !important" },
    "& .MuiPickersDay-root:focus, & .MuiPickersDay-root:focus-visible, & .MuiPickersDay-root.Mui-focusVisible": {
        outline: "none !important",
        borderColor: "#f7b900 !important",
        boxShadow: "0 0 0 1px #f7b900 !important",
    },
    "& .MuiPickersArrowSwitcher-button:hover": {
        bgcolor: "#fff7d6",
        color: "#7a5900",
    },
    "& .MuiPickersArrowSwitcher-button:focus, & .MuiPickersArrowSwitcher-button:focus-visible": {
        bgcolor: "#fff7d6 !important",
        color: "#7a5900 !important",
        outline: "none !important",
        boxShadow: "none !important",
    },
    "& .MuiPickersArrowSwitcher-root": { gap: 0.75 },
    "& .MuiDialogActions-root .MuiButton-root": {
        color: "#7a5900",
        "&:hover, &:focus-visible": {
            bgcolor: "#fff7d6",
        },
    },
    "& .MuiPickersYear-yearButton:hover, & .MuiMonthCalendar-button:hover": { bgcolor: "#fff7d6" },
    "& .MuiPickersYear-yearButton.Mui-selected, & .MuiYearCalendar-button.MuiYearCalendar-selected, & .MuiMonthCalendar-button.Mui-selected, & .MuiMonthCalendar-button.MuiMonthCalendar-selected": {
        bgcolor: "#f7b900 !important",
        color: "#312600 !important",
        "&:hover, &:focus": { bgcolor: "#dca600 !important" },
    },
    "& .MuiYearCalendar-root button[role='radio'][aria-checked='true']": {
        backgroundColor: "#f7b900 !important",
        color: "#312600 !important",
        border: "none !important",
        outline: "none !important",
        boxShadow: "none !important",
    },
    "& .MuiYearCalendar-root button[role='radio'][aria-checked='true']:hover": {
        backgroundColor: "#dca600 !important",
    },
    "& .MuiYearCalendar-root button[role='radio'][aria-checked='true']:focus, & .MuiYearCalendar-root button[role='radio'][aria-checked='true']:focus-visible": {
        backgroundColor: "#f7b900 !important",
        color: "#312600 !important",
        border: "none !important",
        outline: "none !important",
        boxShadow: "none !important",
    },
    "& .MuiPickersYear-yearButton:focus, & .MuiPickersYear-yearButton:focus-visible, & .MuiPickersYear-yearButton.Mui-focusVisible, & .MuiYearCalendar-button:focus, & .MuiYearCalendar-button:focus-visible, & .MuiMonthCalendar-button:focus, & .MuiMonthCalendar-button:focus-visible": {
        outline: "none !important",
        boxShadow: "none !important",
    },
};

const yellowMobileCalendarSx = {
    ...yellowCalendarPopperSx,
    border: "1px solid #efd36c",
    "& .MuiPickersCalendarHeader-switchViewButton": {
        color: "#9a7000",
        "&:hover, &:focus, &:focus-visible": {
            bgcolor: "#fff7d6",
            color: "#7a5900",
        },
    },
    "& .MuiPickersArrowSwitcher-root": {
        gap: 0.75,
    },
    "& .MuiPickersArrowSwitcher-root .MuiIconButton-root": {
        padding: "6px",
    },
    "& .MuiDialogActions-root .MuiButton-root": {
        color: "#7a5900",
        "&:hover, &:focus-visible": {
            bgcolor: "#fff7d6",
        },
    },
};

const CycleDatePickerToolbar = React.forwardRef(function CycleDatePickerToolbar(
    { className, sx, ...props },
    ref,
) {
    const { setValue, setView } = usePickerActionsContext();

    const goToToday = (event) => {
        event.stopPropagation();
        const today = new Date();
        setView("day");
        setValue(today, { changeImportance: "set", shouldClose: false });
    };

    return (
        <Box ref={ref} className={className} sx={[sx, { position: "relative" }]}>
            <DatePickerToolbar {...props} />
            <Button
                size="small"
                variant="outlined"
                onClick={goToToday}
                sx={{
                    position: "absolute",
                    top: 40,
                    right: 24,
                    minWidth: "auto",
                    minHeight: 28,
                    px: 1.25,
                    py: 0.25,
                    borderColor: "#f7b900",
                    color: "#7a5900",
                    fontSize: "0.72rem",
                    textTransform: "none",
                    "&:hover, &:focus-visible": {
                        bgcolor: "#fff7d6",
                        borderColor: "#f7b900",
                    },
                }}
            >
                Today
            </Button>
        </Box>
    );
});

const possessiveLabel = (name) => {
    const clean = String(name || "User").trim() || "User";
    return clean.endsWith("s") ? `${clean}'` : `${clean}'s`;
};

const localIsoDate = (value = new Date()) => {
    const month = String(value.getMonth() + 1).padStart(2, "0");
    const day = String(value.getDate()).padStart(2, "0");
    return `${value.getFullYear()}-${month}-${day}`;
};

const parseLocalDate = (value) => value ? new Date(`${value}T00:00:00`) : null;

const cropLabel = (crop) => CROP_OPTIONS.find((item) => item.value === crop)?.label || crop || "Crop";

const cycleNameFor = (crop, startDate) => {
    const [yearText, monthText] = String(startDate || "").split("-");
    const year = Number(yearText);
    const month = Number(monthText);
    if (!year || !month) return "";
    const firstYear = month >= 7 ? year : year - 1;
    return `${cropLabel(crop)} ${firstYear}/${firstYear + 1}`;
};

const displayDate = (value) => value
    ? new Date(`${value}T00:00:00`).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })
    : "-";

const errorMessage = (error, fallback) => {
    const data = error?.response?.data;
    if (!data) return fallback;
    if (data.detail) return data.detail;
    return Object.values(data).flat().join(" ") || fallback;
};

function StatusChip({ status }) {
    if (!status) return <Chip label="No cycle" size="small" sx={{ bgcolor: "#f1f5f9", color: "#64748b", fontWeight: 700 }} />;
    const active = status === "active";
    return (
        <Chip
            label={active ? "Active" : "Completed"}
            size="small"
            sx={{
                bgcolor: active ? "#dcfce7" : "#e5e7eb",
                color: active ? "#15803d" : "#475569",
                fontWeight: 800,
            }}
        />
    );
}

function CycleHistoryCard({ cycle }) {
    return (
        <Card variant="outlined" sx={{ p: 1.5, boxShadow: "none" }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
                <Box>
                    <Typography sx={{ fontWeight: 700, fontSize: 14 }}>{cycle.name}</Typography>
                    <Typography sx={{ color: "text.secondary", fontSize: 12 }}>
                        {displayDate(cycle.start_date)} – {displayDate(cycle.end_date)}
                    </Typography>
                </Box>
                <StatusChip status={cycle.status} />
            </Stack>
        </Card>
    );
}

export default function FieldsPanel({ username, ownerName, onCountChange }) {
    const [loading, setLoading] = useState(false);
    const [err, setErr] = useState("");
    const [fields, setFields] = useState([]);
    const [count, setCount] = useState(0);
    const [isOwner, setIsOwner] = useState(false);
    const [page, setPage] = useState(1);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editingField, setEditingField] = useState(null);
    const [deletingId, setDeletingId] = useState(null);
    const [fieldToDelete, setFieldToDelete] = useState(null);
    const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

    const [cycleField, setCycleField] = useState(null);
    const [cycleView, setCycleView] = useState("overview");
    const [cycles, setCycles] = useState([]);
    const [cyclesLoading, setCyclesLoading] = useState(false);
    const [cycleSaving, setCycleSaving] = useState(false);
    const [cycleError, setCycleError] = useState("");
    const [cycleToDelete, setCycleToDelete] = useState(null);
    const [cycleToComplete, setCycleToComplete] = useState(null);
    const [olderCyclesExpanded, setOlderCyclesExpanded] = useState(false);
    const [cycleForm, setCycleForm] = useState({ crop: "wheat", start_date: localIsoDate(), name: "", confirm: false });

    const title = isOwner ? "My Fields" : `${possessiveLabel(ownerName)} Fields`;
    const pageCount = Math.max(1, Math.ceil(fields.length / FIELDS_PER_PAGE));
    const visibleFields = useMemo(
        () => fields.slice((page - 1) * FIELDS_PER_PAGE, page * FIELDS_PER_PAGE),
        [fields, page],
    );
    const activeCycle = useMemo(() => cycles.find((cycle) => cycle.status === "active") || null, [cycles]);
    const completedCycles = useMemo(() => cycles.filter((cycle) => cycle.status === "completed"), [cycles]);
    const recentCompletedCycles = completedCycles.slice(0, 3);
    const olderCompletedCycles = completedCycles.slice(3);

    const fetchFields = useCallback(async () => {
        if (!username) return;
        setLoading(true);
        setErr("");
        try {
            const response = await api.get(`/profile/u/${encodeURIComponent(username)}/fields/`);
            const results = response.data?.results ?? [];
            const nextCount = response.data?.count ?? results.length;
            setFields(results);
            setCount(nextCount);
            setIsOwner(Boolean(response.data?.is_owner));
            onCountChange?.(nextCount);
        } catch (error) {
            console.error(error);
            setErr("Failed to load fields.");
            setFields([]);
            setCount(0);
            setIsOwner(false);
            onCountChange?.(0);
        } finally {
            setLoading(false);
        }
    }, [onCountChange, username]);

    const fetchCycles = useCallback(async (field) => {
        if (!field?.id) return;
        setCyclesLoading(true);
        setCycleError("");
        try {
            const response = await api.get(`/fields/mine/${field.id}/crop-cycles/`);
            setCycles(response.data || []);
        } catch (error) {
            setCycles([]);
            setCycleError(errorMessage(error, "Could not load crop cycles."));
        } finally {
            setCyclesLoading(false);
        }
    }, []);

    useEffect(() => { fetchFields(); }, [fetchFields]);
    useEffect(() => {
        if (page > pageCount) setPage(pageCount);
    }, [page, pageCount]);

    const openAdd = () => { setEditingField(null); setDialogOpen(true); };
    const openEdit = (field) => { setEditingField(field); setDialogOpen(true); };

    const deleteField = async () => {
        if (!isOwner || !fieldToDelete?.id) return;
        setDeletingId(fieldToDelete.id);
        try {
            await api.delete(`/fields/mine/${fieldToDelete.id}/`);
            setSnackbar({ open: true, message: `Field "${fieldToDelete.name}" deleted.`, severity: "success" });
            setFieldToDelete(null);
            await fetchFields();
        } catch (error) {
            console.error(error);
            setSnackbar({ open: true, message: "Could not delete field.", severity: "error" });
        } finally {
            setDeletingId(null);
        }
    };

    const openCycleDrawer = async (field) => {
        setCycleField(field);
        setCycleView("overview");
        setCycleError("");
        setCycles([]);
        setOlderCyclesExpanded(false);
        await fetchCycles(field);
    };

    const closeCycleDrawer = () => {
        if (cycleSaving) return;
        setCycleField(null);
        setCycleView("overview");
        setCycleError("");
        setCycleToComplete(null);
    };

    const handleCycleBack = () => {
        if (cycleSaving) return;
        if (cycleView !== "overview") {
            setCycleView("overview");
            setCycleError("");
            return;
        }
        closeCycleDrawer();
    };

    const openNewCycle = () => {
        const fieldCrop = String(cycleField?.crop || "").trim().toLowerCase();
        const crop = CROP_OPTIONS.some((item) => item.value === fieldCrop) ? fieldCrop : "wheat";
        const startDate = localIsoDate();
        setCycleForm({ crop, start_date: startDate, name: cycleNameFor(crop, startDate), confirm: false });
        setCycleError("");
        setCycleView("new");
    };

    const openEditCycle = () => {
        if (!activeCycle) return;
        setCycleForm({
            crop: activeCycle.crop,
            start_date: activeCycle.start_date,
            name: activeCycle.name,
            confirm: false,
        });
        setCycleError("");
        setCycleView("edit");
    };

    const updateCycleStartDate = (startDate) => {
        setCycleForm((previous) => ({ ...previous, start_date: startDate, name: cycleNameFor(previous.crop, startDate) }));
    };

    const createCycle = async () => {
        if (!cycleField?.id) return;
        if (activeCycle && !cycleForm.confirm) {
            setCycleError("Confirm that the current cycle will be completed.");
            return;
        }
        setCycleSaving(true);
        setCycleError("");
        try {
            await api.post(`/fields/mine/${cycleField.id}/crop-cycles/`, {
                crop: cycleForm.crop,
                start_date: cycleForm.start_date,
                name: cycleForm.name,
                complete_current: Boolean(activeCycle && cycleForm.confirm),
            });
            setSnackbar({ open: true, message: `Crop cycle "${cycleForm.name}" started.`, severity: "success" });
            setCycleField((previous) => (previous ? { ...previous, crop: cycleForm.crop } : previous));
            await Promise.all([fetchCycles(cycleField), fetchFields()]);
            setCycleView("overview");
        } catch (error) {
            setCycleError(errorMessage(error, "Could not start the crop cycle."));
        } finally {
            setCycleSaving(false);
        }
    };

    const updateCycle = async () => {
        if (!cycleField?.id || !activeCycle?.id) return;
        setCycleSaving(true);
        setCycleError("");
        try {
            await api.put(`/fields/mine/${cycleField.id}/crop-cycles/${activeCycle.id}/`, {
                crop: cycleForm.crop,
                start_date: cycleForm.start_date,
                name: cycleForm.name,
            });
            setSnackbar({ open: true, message: `Crop cycle "${cycleForm.name}" updated.`, severity: "success" });
            setCycleField((previous) => (previous ? { ...previous, crop: cycleForm.crop } : previous));
            await Promise.all([fetchCycles(cycleField), fetchFields()]);
            setCycleView("overview");
        } catch (error) {
            setCycleError(errorMessage(error, "Could not update the crop cycle."));
        } finally {
            setCycleSaving(false);
        }
    };

    const deleteCycle = async () => {
        if (!cycleField?.id || !cycleToDelete?.id) return;
        setCycleSaving(true);
        setCycleError("");
        try {
            await api.delete(`/fields/mine/${cycleField.id}/crop-cycles/${cycleToDelete.id}/`);
            setSnackbar({ open: true, message: `Crop cycle "${cycleToDelete.name}" deleted.`, severity: "success" });
            setCycleToDelete(null);
            await Promise.all([fetchCycles(cycleField), fetchFields()]);
        } catch (error) {
            setCycleToDelete(null);
            setCycleError(errorMessage(error, "Could not delete the crop cycle."));
        } finally {
            setCycleSaving(false);
        }
    };

    const completeCycle = async () => {
        if (!cycleField?.id || !cycleToComplete?.id) return;
        setCycleSaving(true);
        setCycleError("");
        try {
            await api.post(`/fields/mine/${cycleField.id}/crop-cycles/${cycleToComplete.id}/complete/`, {});
            setSnackbar({ open: true, message: `Crop cycle "${cycleToComplete.name}" completed.`, severity: "success" });
            setCycleToComplete(null);
            await Promise.all([fetchCycles(cycleField), fetchFields()]);
        } catch (error) {
            setCycleToComplete(null);
            setCycleError(errorMessage(error, "Could not complete the crop cycle."));
        } finally {
            setCycleSaving(false);
        }
    };

    return (
        <>
            <Card sx={{
                mb: 3,
                width: "100%",
                bgcolor: "#fff", border: "1px solid #e4e4e4", borderRadius: 2,
                boxShadow: "0 2px 8px rgba(15, 23, 42, 0.08)", overflow: "hidden",
            }}>
                <Box sx={{ display: "flex", flexWrap: "wrap", justifyContent: "space-between", gap: 2, alignItems: "center", px: 2.5, py: 2 }}>
                    <Box>
                        <Typography variant="h6" sx={{ fontWeight: 800 }}>{title}</Typography>
                        <Typography sx={{ color: "text.secondary", fontSize: 14 }}>{loading ? "Loading..." : `${count} registered field(s)`}</Typography>
                    </Box>
                    {isOwner ? <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
                        <Button component="a" href="/templates/SEEDS_field_measurements_template.xlsx" download variant="outlined" startIcon={<DownloadOutlinedIcon />} sx={yellowOutlinedButtonSx}>
                            Download Excel Template
                        </Button>
                        <Button variant="contained" startIcon={<AddIcon />} onClick={openAdd} sx={yellowButtonSx}>Add field</Button>
                    </Stack> : null}
                </Box>
                <Divider />

                <Box sx={{ p: 2.5 }}>
                    {loading ? (
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1, py: 2 }}><CircularProgress size={20} /><Typography color="text.secondary">Fetching fields...</Typography></Box>
                    ) : err ? <Typography color="error">{err}</Typography>
                    : fields.length === 0 ? <Typography sx={{ color: "text.secondary", py: 2 }}>{isOwner ? "No fields yet. Add your first one." : "No public fields."}</Typography>
                    : <>
                        <Stack spacing={1.5}>
                            {visibleFields.map((field) => {
                                const summary = field.active_cycle || field.latest_cycle;
                                return (
                                    <Card key={field.id} variant="outlined" sx={{ position: "relative", p: { xs: 1.5, md: 2 }, pb: { xs: 7, md: 5 }, borderColor: "#e5e5e5", borderRadius: 2, boxShadow: "none" }}>
                                        <Box sx={{
                                            display: "grid",
                                            gridTemplateColumns: isOwner
                                                ? { xs: "1fr", md: "minmax(360px, 1fr) 145px 95px 160px 92px" }
                                                : "1fr",
                                            gap: { xs: 1.5, md: 2 },
                                            alignItems: "center",
                                        }}>
                                            <Box sx={{ minWidth: 0 }}>
                                                <Typography sx={{ fontWeight: 800, color: "#000", overflowWrap: "anywhere" }}>{field.name}</Typography>
                                                <Stack direction="row" spacing={2} useFlexGap flexWrap="wrap" sx={{ mt: 0.75 }}>
                                                    <Stack direction="row" spacing={0.65} alignItems="center">
                                                        <PlaceOutlinedIcon sx={{ fontSize: 16, color: "#6b7280" }} />
                                                        <Typography sx={{ fontSize: 13, color: "#000" }}>{[field.country, field.region].filter(Boolean).join(" - ")}</Typography>
                                                    </Stack>
                                                    <Stack direction="row" spacing={0.65} alignItems="center">
                                                        <SpaOutlinedIcon sx={{ fontSize: 16, color: "#6b7280" }} />
                                                        <Typography sx={{ fontSize: 13, color: "#000" }}>{[cropLabel(field.crop), field.variety].filter(Boolean).join(" - ")}</Typography>
                                                    </Stack>
                                                    {field.acres ? <Stack direction="row" spacing={0.65} alignItems="center">
                                                        <StraightenOutlinedIcon sx={{ fontSize: 16, color: "#6b7280" }} />
                                                        <Typography sx={{ fontSize: 13, color: "#000" }}>{field.acres} ha</Typography>
                                                    </Stack> : null}
                                                    {field.latitude != null && field.longitude != null ? <Stack
                                                        component="a" href={`https://www.google.com/maps?q=${encodeURIComponent(`${field.latitude},${field.longitude}`)}`}
                                                        target="_blank" rel="noopener noreferrer" direction="row" spacing={0.65} alignItems="center"
                                                        aria-label={`Open ${field.name} location in Google Maps`}
                                                        sx={{ color: "#000", textDecoration: "none", "&:hover": { color: "#b77900", textDecoration: "underline" } }}
                                                    >
                                                        <MapOutlinedIcon sx={{ fontSize: 16, color: "inherit" }} />
                                                        <Typography component="span" sx={{ fontSize: 13, color: "inherit" }}>{field.latitude}, {field.longitude}</Typography>
                                                    </Stack> : null}
                                                </Stack>
                                            </Box>

                                            {isOwner ? <>
                                                <Box>
                                                    <Typography sx={{ color: "text.secondary", fontSize: 12 }}>{field.active_cycle ? "Active Cycle" : "Latest Cycle"}</Typography>
                                                    <Typography sx={{ mt: 0.35, color: summary ? "#b77900" : "text.secondary", fontSize: 13, fontWeight: 800 }}>
                                                        {summary?.name || "No crop cycle"}
                                                    </Typography>
                                                </Box>
                                                <Box>
                                                    <Typography sx={{ color: "text.secondary", fontSize: 12, mb: 0.5 }}>Status</Typography>
                                                    <StatusChip status={summary?.status} />
                                                </Box>
                                                <Button variant="outlined" onClick={() => openCycleDrawer(field)} sx={{ ...yellowOutlinedButtonSx, fontSize: 12}}>
                                                    Manage Crop Cycles
                                                </Button>
                                                <Stack
                                                    alignItems={{ xs: "flex-start", md: "flex-end" }}
                                                    sx={{
                                                        alignSelf: "stretch",
                                                        position: { xs: "absolute", md: "static" },
                                                        left: { xs: 12, md: "auto" },
                                                        bottom: { xs: 10, md: "auto" },
                                                    }}
                                                >
                                                    <Stack direction="row" spacing={0.25}>
                                                        <Tooltip title="Edit field"><IconButton size="small" aria-label={`Edit ${field.name}`} onClick={() => openEdit(field)} sx={{ color: "#4b5563", border: "1px solid #e5e7eb", borderRadius: 1.25, "&:hover": { color: "#111", bgcolor: "#fff7d6" } }}><EditOutlinedIcon fontSize="small" /></IconButton></Tooltip>
                                                        <Tooltip title="Delete field"><IconButton size="small" aria-label={`Delete ${field.name}`} onClick={() => setFieldToDelete(field)} disabled={deletingId === field.id} sx={{ color: "#6b7280", border: "1px solid #e5e7eb", borderRadius: 1.25, "&:hover": { color: "#dc2626", bgcolor: "#fff1f2" } }}><DeleteOutlineIcon fontSize="small" /></IconButton></Tooltip>
                                                    </Stack>
                                                </Stack>
                                            </> : null}
                                        </Box>
                                        {isOwner ? <Chip
                                            label={field.is_public ? "Public" : "Private"}
                                            size="small"
                                            sx={{
                                                position: "absolute",
                                                right: { xs: 12, md: 16 },
                                                bottom: { xs: 10, md: 12 },
                                                height: 22,
                                                fontWeight: 700,
                                                color: field.is_public ? "#312600" : "#475569",
                                                bgcolor: field.is_public ? "#f7b900" : "#f1f5f9",
                                            }}
                                        /> : null}
                                    </Card>
                                );
                            })}
                        </Stack>
                        {fields.length > FIELDS_PER_PAGE ? <Stack alignItems="center" sx={{ mt: 3 }}>
                            <Pagination count={pageCount} page={page} onChange={(_, value) => setPage(value)} showFirstButton showLastButton siblingCount={1} boundaryCount={1} sx={{
                                "& .MuiPagination-ul": { gap: 0.75 },
                                "& .MuiPaginationItem-root": { border: "1px solid #efd36c", color: "#9a7000", bgcolor: "#fff", minWidth: 38, height: 38, borderRadius: 1.25, fontSize: 13 },
                                "& .MuiPaginationItem-root.Mui-selected": { color: "#312600", bgcolor: "#f7b900", backgroundImage: "none", borderColor: "#f7b900", "&:hover": { bgcolor: "#dca600" } },
                            }} />
                        </Stack> : null}
                    </>}
                </Box>
            </Card>

            <Drawer
                anchor="right"
                open={Boolean(cycleField)}
                onClose={closeCycleDrawer}
                slotProps={{
                    paper: {
                        sx: {
                            width: { xs: "100%", sm: 440 },
                            top: "70px",
                            height: "calc(100% - 70px)",
                            p: 0,
                        },
                    },
                }}
            >
                <Box sx={{ px: 2.5, py: 2 }}>
                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                        <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
                            <IconButton
                                aria-label="Back"
                                onClick={handleCycleBack}
                                disabled={cycleSaving}
                                sx={{
                                    display: { xs: "inline-flex", md: "none" },
                                    flex: "0 0 auto",
                                    ml: -1,
                                    color: "#312600",
                                    "&:hover, &:focus-visible": { bgcolor: "#fff7d6" },
                                }}
                            >
                                <ArrowBackRoundedIcon />
                            </IconButton>
                            {cycleView !== "overview" ? <IconButton size="small" onClick={() => { setCycleView("overview"); setCycleError(""); }} disabled={cycleSaving} sx={{ display: { xs: "none", md: "inline-flex" } }}><ArrowBackRoundedIcon /></IconButton> : null}
                            <Box sx={{ minWidth: 0 }}>
                                <Typography variant="h6" sx={{ fontWeight: 800, overflowWrap: "anywhere" }}>
                                    {cycleView === "new" ? "Start New Crop Cycle" : cycleView === "edit" ? "Edit Crop Cycle" : "Crop Cycles"}
                                </Typography>
                                <Typography sx={{ color: "text.secondary", fontSize: 13, overflowWrap: "anywhere" }}>{cycleField?.name}</Typography>
                            </Box>
                        </Stack>
                        <IconButton aria-label="Close crop cycles" onClick={closeCycleDrawer} disabled={cycleSaving} sx={{ display: { xs: "none", md: "inline-flex" } }}><CloseRoundedIcon /></IconButton>
                    </Box>
                </Box>
                <Divider />

                <Box sx={{ p: 2.5, overflowY: "auto" }}>
                    {cyclesLoading ? <Stack alignItems="center" sx={{ py: 8 }}><CircularProgress size={28} /></Stack>
                    : cycleView === "overview" ? (
                        <Stack spacing={3}>
                            {cycleError ? <Alert severity="error">{cycleError}</Alert> : null}
                            <Box>
                                <Typography variant="overline" sx={{ fontWeight: 800, color: "text.secondary" }}>Current cycle</Typography>
                                {activeCycle ? <Card variant="outlined" sx={{ mt: 0.75, p: 2, borderColor: "#d7e8dc", bgcolor: "#fbfffc" }}>
                                    <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={2}>
                                        <Box>
                                            <Typography sx={{ fontWeight: 800 }}>{activeCycle.name}</Typography>
                                            <Typography sx={{ mt: 0.5, color: "text.secondary", fontSize: 13 }}>Started {displayDate(activeCycle.start_date)}</Typography>
                                        </Box>
                                        <StatusChip status={activeCycle.status} />
                                    </Stack>
                                    <Stack direction="row" justifyContent="space-between" alignItems="flex-end" spacing={1} sx={{ mt: 2 }}>
                                        <Button variant="outlined" onClick={() => setCycleToComplete(activeCycle)} disabled={cycleSaving} sx={yellowOutlinedButtonSx}>
                                            {cycleSaving ? "Completing..." : "Complete cycle"}
                                        </Button>
                                        <Stack direction="row" spacing={0.25}>
                                            <Tooltip title="Edit current cycle">
                                                <IconButton size="small" aria-label="Edit current crop cycle" onClick={openEditCycle} disabled={cycleSaving} sx={{ color: "#4b5563", "&:hover": { color: "#111", bgcolor: "#fff7d6" } }}>
                                                    <EditOutlinedIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title="Delete current cycle">
                                                <IconButton size="small" aria-label="Delete current crop cycle" onClick={() => setCycleToDelete(activeCycle)} disabled={cycleSaving} sx={{ color: "#6b7280", "&:hover": { color: "#dc2626", bgcolor: "#fff1f2" } }}>
                                                    <DeleteOutlineIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        </Stack>
                                    </Stack>
                                </Card> : <Alert severity="info" sx={{ mt: 0.75 }}>This field does not have an active crop cycle.</Alert>}
                            </Box>

                            <Box>
                                <Typography variant="overline" sx={{ fontWeight: 800, color: "text.secondary" }}>History</Typography>
                                {/* <Typography sx={{ mt: 0.25, color: "text.secondary", fontSize: 12 }}>
                                    Completed cycles and their daily data are read-only.
                                </Typography> */}
                                <Stack spacing={1} sx={{ mt: 0.75 }}>
                                    {recentCompletedCycles.length ? recentCompletedCycles.map((cycle) => (
                                        <Card key={cycle.id} variant="outlined" sx={{ p: 1.5, boxShadow: "none" }}>
                                            <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
                                                <Box>
                                                    <Typography sx={{ fontWeight: 700, fontSize: 14 }}>{cycle.name}</Typography>
                                                    <Typography sx={{ color: "text.secondary", fontSize: 12 }}>{displayDate(cycle.start_date)} – {displayDate(cycle.end_date)}</Typography>
                                                </Box>
                                                <StatusChip status={cycle.status} />
                                            </Stack>
                                        </Card>
                                    )) : <Typography sx={{ color: "text.secondary", fontSize: 14 }}>No completed crop cycles yet.</Typography>}
                                    {olderCompletedCycles.length ? (
                                        <Accordion
                                            expanded={olderCyclesExpanded}
                                            onChange={(_, expanded) => setOlderCyclesExpanded(expanded)}
                                            disableGutters
                                            elevation={0}
                                            sx={{
                                                border: "1px solid #efd36c",
                                                borderRadius: "10px !important",
                                                overflow: "hidden",
                                                "&:before": { display: "none" },
                                            }}
                                        >
                                            <AccordionSummary
                                                expandIcon={<ExpandMoreRoundedIcon sx={{ color: "#7a5900" }} />}
                                                aria-controls="older-crop-cycles-content"
                                                id="older-crop-cycles-header"
                                                sx={{
                                                    minHeight: 44,
                                                    bgcolor: "#fffdf5",
                                                    color: "#7a5900",
                                                    "&.Mui-expanded": { minHeight: 44 },
                                                    "& .MuiAccordionSummary-content, & .MuiAccordionSummary-content.Mui-expanded": { my: 1 },
                                                }}
                                            >
                                                <Typography sx={{ fontSize: 13, fontWeight: 700 }}>
                                                    {olderCyclesExpanded
                                                        ? "Hide older cycles"
                                                        : `Show ${olderCompletedCycles.length} older ${olderCompletedCycles.length === 1 ? "cycle" : "cycles"}`}
                                                </Typography>
                                            </AccordionSummary>
                                            <AccordionDetails sx={{ p: 1.25, borderTop: "1px solid #f3e3a5" }}>
                                                <Stack spacing={1}>
                                                    {olderCompletedCycles.map((cycle) => (
                                                        <CycleHistoryCard key={cycle.id} cycle={cycle} />
                                                    ))}
                                                </Stack>
                                            </AccordionDetails>
                                        </Accordion>
                                    ) : null}
                                </Stack>
                            </Box>

                            <Button fullWidth variant="contained" startIcon={<AddIcon />} onClick={openNewCycle} sx={yellowButtonSx}>
                                Start New Crop Cycle
                            </Button>
                        </Stack>
                    ) : (
                        <Stack spacing={2.25}>
                            {cycleView === "new" && activeCycle ? <Alert severity="warning">
                                <AlertTitle>Current active cycle</AlertTitle>
                                Starting a new cycle will complete <strong>{activeCycle.name}</strong> on the day before the new cycle starts.
                            </Alert> : null}
                            {cycleError ? <Alert severity="error">{cycleError}</Alert> : null}

                            <TextField
                                fullWidth
                                label="Crop"
                                value={cropLabel(cycleForm.crop)}
                                disabled
                                helperText="Crop is defined in My Fields."
                                sx={cycleFieldSx}
                            />
                            <LocalizationProvider dateAdapter={AdapterDateFns}>
                                <DatePicker
                                    label="Start date"
                                    value={parseLocalDate(cycleForm.start_date)}
                                    onChange={(value) => updateCycleStartDate(value ? localIsoDate(value) : "")}
                                    format="dd/MM/yyyy"
                                    closeOnSelect={false}
                                    slots={{ toolbar: CycleDatePickerToolbar }}
                                    slotProps={{
                                        textField: { fullWidth: true, sx: cycleFieldSx },
                                        popper: { sx: yellowCalendarPopperSx },
                                        desktopPaper: { sx: { ...yellowCalendarPopperSx, border: "1px solid #efd36c" } },
                                        mobilePaper: { sx: yellowMobileCalendarSx },
                                        toolbar: { hidden: false },
                                        actionBar: { actions: ["cancel", "accept"] },
                                        yearButton: {
                                            sx: {
                                                border: "none !important",
                                                outline: "none !important",
                                                boxShadow: "none !important",
                                                "&:hover": { bgcolor: "#fff7d6 !important" },
                                                "&:focus, &:focus-visible": {
                                                    bgcolor: "transparent !important",
                                                    border: "none !important",
                                                    outline: "none !important",
                                                    boxShadow: "none !important",
                                                },
                                                "&.MuiYearCalendar-selected": {
                                                    bgcolor: "#f7b900 !important",
                                                    color: "#312600 !important",
                                                    border: "none !important",
                                                    outline: "none !important",
                                                    boxShadow: "none !important",
                                                },
                                                "&.MuiYearCalendar-selected:focus, &.MuiYearCalendar-selected:focus-visible": {
                                                    bgcolor: "#f7b900 !important",
                                                    border: "none !important",
                                                    outline: "none !important",
                                                    boxShadow: "none !important",
                                                },
                                            },
                                        },
                                        openPickerButton: {
                                            sx: {
                                                border: "none !important",
                                                bgcolor: "transparent !important",
                                                boxShadow: "none !important",
                                                color: "#9a7000",
                                                "&:hover, &:focus, &:focus-visible": {
                                                    border: "none !important",
                                                    bgcolor: "#fff7d6 !important",
                                                    boxShadow: "none !important",
                                                    outline: "none !important",
                                                },
                                            },
                                        },
                                        previousIconButton: {
                                            sx: {
                                                mr: { xs: 0.5, sm: 0 },
                                                "&:hover": { bgcolor: "#fff7d6", color: "#7a5900" },
                                            },
                                        },
                                        nextIconButton: { sx: { "&:hover": { bgcolor: "#fff7d6", color: "#7a5900" } } },
                                    }}
                                />
                            </LocalizationProvider>
                            <TextField fullWidth label="Cycle name" value={cycleForm.name} onChange={(event) => setCycleForm((previous) => ({ ...previous, name: event.target.value }))} helperText="Generated automatically from crop and start date; you can edit it." sx={cycleFieldSx} />

                            {cycleView === "new" && activeCycle ? <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={cycleForm.confirm}
                                        onChange={(event) => setCycleForm((previous) => ({ ...previous, confirm: event.target.checked }))}
                                        disableRipple
                                        icon={<Box sx={{ width: 16, height: 16 }} />}
                                        checkedIcon={<CheckRoundedIcon sx={{ fontSize: 17 }} />}
                                        sx={{
                                            "&&": {
                                                width: 22,
                                                height: 22,
                                                m: "10px",
                                                p: 0,
                                                boxSizing: "border-box",
                                                color: "#312600",
                                                border: "2px solid #dca600",
                                                borderRadius: "6px",
                                                bgcolor: "#fffdf5",
                                                boxShadow: "none",
                                            },
                                            "&&:hover": { borderColor: "#f7b900", bgcolor: "#fff7d6" },
                                            "&&.Mui-checked": {
                                                color: "#312600",
                                                borderColor: "#f7b900",
                                                bgcolor: "#f7b900",
                                                boxShadow: "none",
                                            },
                                            "&&.Mui-checked:hover": { bgcolor: "#dca600", borderColor: "#dca600" },
                                            "&&.Mui-focusVisible": {
                                                borderColor: "#f7b900",
                                                outline: "3px solid rgba(247, 185, 0, 0.28)",
                                                outlineOffset: 2,
                                            },
                                        }}
                                    />
                                }
                                label={`I understand that ${activeCycle.name} will be completed.`}
                            /> : null}

                            <Stack direction="row" justifyContent="flex-end" spacing={1} sx={{ pt: 1 }}>
                                <Button onClick={() => { setCycleView("overview"); setCycleError(""); }} disabled={cycleSaving}>Cancel</Button>
                                <Button
                                    variant="contained"
                                    onClick={cycleView === "edit" ? updateCycle : createCycle}
                                    disabled={cycleSaving || !cycleForm.crop || !cycleForm.start_date || !cycleForm.name.trim() || Boolean(cycleView === "new" && activeCycle && !cycleForm.confirm)}
                                    sx={yellowButtonSx}
                                >
                                    {cycleSaving ? "Saving..." : cycleView === "edit" ? "Save changes" : "Start cycle"}
                                </Button>
                            </Stack>
                        </Stack>
                    )}
                </Box>
            </Drawer>

            <DeleteConfirmDialog
                open={Boolean(cycleToComplete)}
                title="Complete this crop cycle?"
                description={`Are you sure you want to complete "${cycleToComplete?.name || "this crop cycle"}"? Its end date will be set and it will move to the cycle history.`}
                confirmLabel="Complete cycle"
                loadingLabel="Completing..."
                loading={cycleSaving}
                onClose={() => setCycleToComplete(null)}
                onConfirm={completeCycle}
            />

            <AddFieldDialog
                open={dialogOpen}
                field={editingField}
                onClose={() => { setDialogOpen(false); setEditingField(null); }}
                onCreated={fetchFields}
                onSaved={({ isEditing, name }) => setSnackbar({ open: true, message: isEditing ? `Field "${name}" updated successfully.` : `Field "${name}" added successfully.`, severity: "success" })}
                isOwner={isOwner}
            />
            <DeleteConfirmDialog
                open={Boolean(cycleToDelete)}
                title="Do you want to delete this crop cycle?"
                description={`Are you sure you want to delete "${cycleToDelete?.name || "this crop cycle"}"? A cycle with daily data cannot be deleted and should be completed instead.`}
                loading={cycleSaving}
                onClose={() => setCycleToDelete(null)}
                onConfirm={deleteCycle}
            />
            <DeleteConfirmDialog open={Boolean(fieldToDelete)} title="Do you want to delete this field?" description={`Are you sure you want to delete "${fieldToDelete?.name || "this field"}"? This action cannot be undone.`} loading={Boolean(deletingId)} onClose={() => setFieldToDelete(null)} onConfirm={deleteField} />
            <AppSnackbar open={snackbar.open} message={snackbar.message} severity={snackbar.severity} onClose={() => setSnackbar((previous) => ({ ...previous, open: false }))} />
        </>
    );
}
